import getExternalSceneExport from '@cartwhl/common/api/getExternalSceneExport';
import postExternalSceneExport, {
  type SceneExportSettings,
  type SceneExportStatus,
  type SceneObjectExport,
  type SceneExportResponse,
} from '@cartwhl/common/api/postExternalSceneExport';
import {
  sanitizePromptAsFilename,
  triggerBrowserFileDownload,
  waitForAsyncState,
} from '@cartwhl/common/utils';
import { proxy } from 'valtio';

import { noticesStore, userStore } from './global';

export const SCENE_EXPORT_POLLING_INTERVAL_MS = 15000;
export const ESTIMATED_SECONDS_PER_SCENE_OBJECT = 90;

export type SceneExportJobStatus =
  | 'creating'
  | 'pending'
  | 'ready'
  | 'error';

export type SceneExportJob = {
  sceneID: string;
  sceneName: string;
  objectReferenceNames?: string[];
  objectReferenceCount: number;
  exportSettings: SceneExportSettings;
  status: SceneExportJobStatus;
  exports: SceneObjectExport[];
  startedAt: string;
  estimatedFinishTime: string;
  downloadURL?: string;
  error?: string;
  lastPollError?: string;
  lastUpdatedAt?: string;
  autoDownloadStarted: boolean;
};

type State = {
  sceneExports: Record<string, SceneExportJob>;
};

export const state = proxy<State>({
  sceneExports: {},
});

const pollingIntervals = new Map<string, ReturnType<typeof setInterval>>();
const pollingInFlight = new Set<string>();

export function isSceneExportInProgress(
  exportJob?: Pick<SceneExportJob, 'status'> | null,
) {
  return exportJob?.status === 'creating' || exportJob?.status === 'pending';
}

export async function createSceneExport({
  sceneID,
  sceneName,
  objectReferenceNames,
  objectReferenceCount,
  exportSettings,
}: {
  sceneID: string;
  sceneName: string;
  objectReferenceNames?: string[];
  objectReferenceCount: number;
  exportSettings: SceneExportSettings;
}) {
  if (isSceneExportInProgress(state.sceneExports[sceneID])) {
    return;
  }

  const requestedObjectCount =
    objectReferenceNames?.length ?? objectReferenceCount;
  if (requestedObjectCount <= 0) {
    noticesStore.error('This scene does not have any objects to export.');
    return;
  }

  const startedAt = new Date();
  const estimatedFinishTime = new Date(
    startedAt.getTime() +
    requestedObjectCount * ESTIMATED_SECONDS_PER_SCENE_OBJECT * 1000,
  );

  state.sceneExports[sceneID] = {
    sceneID,
    sceneName,
    objectReferenceNames,
    objectReferenceCount: requestedObjectCount,
    exportSettings,
    status: 'creating',
    exports: [],
    startedAt: startedAt.toISOString(),
    estimatedFinishTime: estimatedFinishTime.toISOString(),
    autoDownloadStarted: false,
  };

  let apiKey: string;
  try {
    apiKey = await getApiKey();
  } catch (error) {
    console.error('[SceneExport] Failed to get API key:', error);
    setSceneExportError(sceneID, 'Failed to authenticate. Please try again.');
    noticesStore.error('Failed to authenticate. Please try again.');
    return;
  }

  const { data, error } = await postExternalSceneExport(apiKey, sceneID, {
    ...(objectReferenceNames?.length
      ? { objectReferenceNamesToExport: objectReferenceNames }
      : {}),
    exportSettings,
  });

  if (error || !data) {
    const message = error?.message ?? 'Failed to start scene export.';
    console.error('[SceneExport] Failed to start scene export:', error);
    setSceneExportError(sceneID, message);
    noticesStore.error(message);
    return;
  }

  updateSceneExportFromResponse(sceneID, data);
  if (isSceneExportInProgress(state.sceneExports[sceneID])) {
    startSceneExportPolling(sceneID);
  }
  await autoDownloadSceneExportIfReady(sceneID);
}

export async function pollSceneExport(sceneID: string) {
  const exportJob = state.sceneExports[sceneID];
  if (!isSceneExportInProgress(exportJob)) {
    stopSceneExportPolling(sceneID);
    return;
  }
  if (pollingInFlight.has(sceneID)) {
    return;
  }

  pollingInFlight.add(sceneID);
  try {
    const apiKey = await getApiKey();
    const { data, error } = await getExternalSceneExport(
      apiKey,
      sceneID,
      exportJob.objectReferenceNames,
    );

    if (error || !data) {
      const message = error?.message ?? 'Failed to check scene export status.';
      exportJob.lastPollError = message;
      console.error('[SceneExport] Failed to poll scene export:', error);
      return;
    }

    updateSceneExportFromResponse(sceneID, data);
    await autoDownloadSceneExportIfReady(sceneID);
  } catch (error) {
    exportJob.lastPollError = 'Failed to check scene export status.';
    console.error('[SceneExport] Failed to poll scene export:', error);
  } finally {
    pollingInFlight.delete(sceneID);
  }
}

export async function downloadSceneExport(sceneID: string) {
  const exportJob = state.sceneExports[sceneID];
  if (!exportJob?.downloadURL) {
    return;
  }

  await triggerSceneExportDownload(exportJob, true);
}

function startSceneExportPolling(sceneID: string) {
  if (pollingIntervals.has(sceneID)) {
    return;
  }

  const interval = setInterval(() => {
    void pollSceneExport(sceneID);
  }, SCENE_EXPORT_POLLING_INTERVAL_MS);

  pollingIntervals.set(sceneID, interval);
}

function stopSceneExportPolling(sceneID: string) {
  const interval = pollingIntervals.get(sceneID);
  if (!interval) {
    return;
  }

  clearInterval(interval);
  pollingIntervals.delete(sceneID);
}

function updateSceneExportFromResponse(
  sceneID: string,
  response: SceneExportResponse,
) {
  const exportJob = state.sceneExports[sceneID];
  if (!exportJob) {
    return;
  }

  exportJob.exports = response.exports ?? [];
  exportJob.downloadURL = response.downloadURL;
  exportJob.lastPollError = undefined;
  exportJob.lastUpdatedAt = new Date().toISOString();

  if (exportJob.exports.some(isFailedExportStatus)) {
    setSceneExportError(sceneID, 'Scene export failed. Please try again.');
    stopSceneExportPolling(sceneID);
    noticesStore.error('Scene export failed. Please try again.');
    return;
  }

  if (
    response.downloadURL &&
    exportJob.exports.length >= exportJob.objectReferenceCount &&
    exportJob.exports.every(
      (exportRecord) => exportRecord.status === 'COMPLETED',
    )
  ) {
    exportJob.status = 'ready';
    stopSceneExportPolling(sceneID);
    return;
  }

  exportJob.status = 'pending';
}

function setSceneExportError(sceneID: string, message: string) {
  const exportJob = state.sceneExports[sceneID];
  if (!exportJob) {
    return;
  }

  exportJob.status = 'error';
  exportJob.error = message;
  exportJob.lastUpdatedAt = new Date().toISOString();
  stopSceneExportPolling(sceneID);
}

async function autoDownloadSceneExportIfReady(sceneID: string) {
  const exportJob = state.sceneExports[sceneID];
  if (
    exportJob?.status !== 'ready' ||
    !exportJob.downloadURL ||
    exportJob.autoDownloadStarted
  ) {
    return;
  }

  exportJob.autoDownloadStarted = true;
  const downloaded = await triggerSceneExportDownload(exportJob, false);
  if (downloaded) {
    noticesStore.success('Scene export ready.');
  } else {
    noticesStore.error(
      'Scene export is ready, but the automatic download failed.',
    );
  }
}

async function triggerSceneExportDownload(
  exportJob: SceneExportJob,
  showErrorNotice: boolean,
) {
  if (!exportJob.downloadURL) {
    return false;
  }

  try {
    await triggerBrowserFileDownload(
      exportJob.downloadURL,
      getSceneExportFilename(exportJob),
    );
    return true;
  } catch (error) {
    console.error('[SceneExport] Failed to download scene export:', error);
    if (showErrorNotice) {
      noticesStore.error('Download failed. Please try again.');
    }
    return false;
  }
}

function isFailedExportStatus(exportRecord: {
  status: SceneExportStatus | string;
}) {
  return (
    exportRecord.status === 'FAILED' ||
    exportRecord.status === 'REJECTED' ||
    exportRecord.status === 'CANCELLED'
  );
}

async function getApiKey() {
  const apiKeyData = await waitForAsyncState(
    userStore.state,
    (userState) => userState.apiKey,
  );

  if (!apiKeyData?.apiKey) {
    throw new Error('API key not available');
  }

  return apiKeyData.apiKey;
}

function getSceneExportFilename(exportJob: SceneExportJob) {
  const sceneSlug = sanitizePromptAsFilename(exportJob.sceneName, 80);
  return `Cartwheel-${sceneSlug || exportJob.sceneID}.zip`;
}
